// frontend/src/components/common/ModuloHeader.jsx
//
// Encabezado unificado de los módulos de administración de datos
// (Documentación, Partes, Reparaciones, Almacén, Lubricantes, Mantenimiento,
// Logística y Combustible).
//
// La disposición —ícono + título, botón de Ayuda al lado, descripción debajo y
// pestañas abajo de todo, con las tarjetas de estado a la derecha— es la de
// Reparaciones, que se tomó como referencia visual del sistema. Cualquier
// módulo nuevo de este tipo debe usar este componente en lugar de rearmar su
// propio encabezado: es lo que mantiene la armonía al pasar de una pantalla a
// otra.
//
// Debajo del encabezado, la barra de sección (SeccionBar) agrupa el título de
// la lista visible con su buscador, filtros y botones de acción.

import React from 'react';
import styled from 'styled-components';
import HelpPanel, { HelpToggleBtn } from './HelpPanel';
import { PageTypographyScope } from './OperationalVisuals.jsx';

// Contenedor de página de un módulo: ocupa el alto disponible y deja que la
// lista de abajo sea la que scrollea.
export const ModuloShell = styled(PageTypographyScope)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

// ============================= ENCABEZADO =============================

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 1fr) auto;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 4px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-left: 8px;

  @media (max-width: 760px) {
    padding-left: 0;
    margin-top: 6px;
  }

  h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 20px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: 1.05;
  }

  small {
    color: ${({ theme }) => theme.colors.txtSecondary};
    font-size: 11px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Tabs = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const TabButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? theme.colors.onPrimary : theme.colors.txtPrincipal)};
  border-radius: 8px;
  padding: 7px 11px;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 10px;

  @media (max-width: 1100px) {
    justify-content: flex-start;
  }
`;

const HelpWrap = styled.div`
  margin-bottom: 10px;
`;

/**
 * Encabezado de módulo.
 *
 * @param {string}  icono       Clase del ícono (el mismo de la cinta de opciones).
 * @param {string}  titulo      Nombre del módulo.
 * @param {string}  descripcion Una línea de qué se administra acá.
 * @param {object}  help        { prereqs, tips, nextSteps } — sin esto no se muestra Ayuda.
 * @param {boolean} helpOpen    Estado del panel de ayuda (lo controla la página).
 * @param {func}    onToggleHelp
 * @param {array}   tabs        [{ key, label, icon }] — opcional.
 * @param {string}  tabActiva
 * @param {func}    onTabChange
 * @param {node}    stats       Tarjetas de estado, a la derecha.
 */
export const ModuloHeader = ({
  icono,
  titulo,
  descripcion,
  help,
  helpOpen = false,
  onToggleHelp,
  tabs,
  tabActiva,
  onTabChange,
  stats,
}) => (
  <>
    <HeaderGrid>
      <TitleBlock>
        <TitleRow>
          <h2>
            {icono ? <i className={icono} /> : null}
            {titulo}
          </h2>
          {help && onToggleHelp && (
            <HelpToggleBtn type="button" $active={helpOpen} onClick={onToggleHelp}>
              <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
              Ayuda
            </HelpToggleBtn>
          )}
        </TitleRow>
        {descripcion ? <small>{descripcion}</small> : null}
        {tabs && tabs.length > 0 && (
          <Tabs>
            {tabs.map((t) => (
              <TabButton
                key={t.key}
                type="button"
                $active={tabActiva === t.key}
                onClick={() => onTabChange && onTabChange(t.key)}
              >
                {t.icon ? <i className={t.icon} /> : null}
                {t.label}
              </TabButton>
            ))}
          </Tabs>
        )}
      </TitleBlock>

      {stats ? <StatsRow>{stats}</StatsRow> : <div />}
    </HeaderGrid>

    {help && helpOpen && (
      <HelpWrap>
        <HelpPanel
          prereqs={help.prereqs}
          tips={help.tips}
          nextSteps={help.nextSteps}
          inlineOpen
        />
      </HelpWrap>
    )}
  </>
);

// ============================= BARRA DE SECCIÓN =============================

export const SeccionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  padding: 4px 8px;
  margin: 2px 0;
  flex-shrink: 0;
`;

export const SeccionTitulo = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SeccionAcciones = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

// Buscador de la barra de sección: mismo alto (30px) que los botones de al lado.
const BuscadorWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const BuscadorIcono = styled.i`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: ${({ theme }) => theme.colors.txtSecondary};
  pointer-events: none;
`;

const BuscadorInput = styled.input`
  width: ${({ $ancho }) => $ancho || 260}px;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  height: 30px;
  padding: 0 26px 0 28px;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.info};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.txtMuted};
  }
`;

const BuscadorLimpiar = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.colors.transparent};
  border: none;
  color: ${({ theme }) => theme.colors.txtSecondary};
  cursor: pointer;
  padding: 0;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.info};
  }
`;

export const SeccionBuscador = ({ value, onChange, placeholder, ancho, ...props }) => (
  <BuscadorWrap>
    <BuscadorIcono className="bi bi-search" />
    <BuscadorInput
      type="text"
      $ancho={ancho}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...props}
    />
    {value ? (
      <BuscadorLimpiar type="button" onClick={() => onChange('')} title="Limpiar búsqueda">
        <i className="bi bi-x-circle" style={{ fontSize: 16 }} />
      </BuscadorLimpiar>
    ) : null}
  </BuscadorWrap>
);

// Alto compartido por los botones de la barra de sección, para que queden
// alineados con el buscador.
export const ESTILO_BOTON_SECCION = {
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
};

export default ModuloHeader;
